import { useEffect, useState } from "react";
import { CreditCard, Check, Users } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

export default function BillingAdmin() {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState("");

  const load = () => api.get("/school/billing/status").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const subscribe = async (plan) => {
    setPaying(plan.id);
    try {
      localStorage.setItem("timely_pay_return", "/admin/billing");
      const { data } = await api.post("/payments/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setPaying("");
    }
  };

  const manageBilling = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) return <div className="font-display font-bold animate-pulse" data-testid="billing-loading">Loading…</div>;

  const statusLabel = data.fees_waived ? "Fees waived" : data.subscription_status === "active" ? "Active" : data.subscription_status === "trialing" ? "Free trial" : "Inactive";
  const statusBg = data.fees_waived ? "#D8B4FE" : data.subscription_status === "active" ? "#A7E4D3" : data.subscription_status === "trialing" ? "#FDE68A" : "#FECACA";
  const trialDays = data.trial_ends_at ? Math.max(0, Math.ceil((new Date(data.trial_ends_at) - Date.now()) / 86400000)) : null;

  return (
    <div data-testid="billing-page" className="max-w-4xl">
      <span className="nb-label">Billing</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Subscription & plan</h1>

      {!data.billing_ok && (
        <div className="mt-6 nb-card p-5 bg-red-100" data-testid="billing-locked-banner">
          <p className="font-bold text-red-800">Your trial or subscription has ended. Subscribe below to reactivate pupil sign-ups and admin features.</p>
        </div>
      )}

      <div className="mt-6 nb-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span className="nb-label">Current status</span>
            <div className="flex items-center gap-3 mt-2">
              <span className="px-3 py-1.5 rounded-xl border-2 border-zinc-900 font-bold text-sm" style={{ backgroundColor: statusBg }} data-testid="billing-status-badge">{statusLabel}</span>
              {data.plan && <span className="font-display font-bold text-lg" data-testid="billing-current-plan">{data.plan.name}</span>}
            </div>
            <p className="text-sm text-zinc-500 mt-2">
              {data.fees_waived && "The platform team has waived your fees. Enjoy!"}
              {data.subscription_status === "trialing" && trialDays !== null && `${trialDays} day(s) left of your free trial.`}
              {data.subscription_status === "active" && data.current_period_end && `Renews around ${new Date(data.current_period_end).toLocaleDateString("en-GB")}.`}
            </p>
            {data.subscription_status === "active" && !data.fees_waived && (
              <button onClick={manageBilling} data-testid="billing-manage-btn" className="nb-btn bg-white px-4 py-2 text-sm mt-3">
                <CreditCard className="w-4 h-4" /> Manage billing — card, invoices, cancel
              </button>
            )}
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <Users className="w-5 h-5 text-zinc-400" />
              <span className="font-display font-black text-3xl" data-testid="billing-pupil-usage">{data.pupils_count}</span>
              {data.plan && <span className="font-bold text-zinc-500">/ {data.plan.max_pupils} pupils</span>}
            </div>
            <p className="text-xs text-zinc-400 font-semibold mt-1">Pupil accounts used</p>
          </div>
        </div>
      </div>

      <h2 className="font-display font-bold text-xl mt-8">Available plans</h2>
      <div className="mt-4 grid sm:grid-cols-3 gap-5">
        {data.plans.map((p) => {
          const current = data.plan?.id === p.id;
          return (
            <div key={p.id} className={`nb-card p-6 flex flex-col ${current ? "bg-[#A7E4D3]/40" : "bg-white"}`} data-testid={`billing-plan-${p.id}`}>
              <h3 className="font-display font-bold text-lg">{p.name}</h3>
              <div className="mt-2">
                <span className="font-display font-black text-3xl">£{Number(p.price_gbp).toFixed(2)}</span>
                <span className="font-bold text-sm text-zinc-500"> / year</span>
              </div>
              <p className="text-sm text-zinc-500 mt-2 flex-1">Up to {p.max_pupils} pupil accounts</p>
              {current ? (
                <span className="mt-4 inline-flex items-center gap-2 font-bold text-sm text-zinc-600" data-testid={`billing-plan-current-${p.id}`}><Check className="w-4 h-4" /> Current plan</span>
              ) : (
                <button onClick={() => subscribe(p)} disabled={!!paying || data.fees_waived} data-testid={`billing-subscribe-${p.id}`}
                  className="nb-btn bg-[#1E3A8A] text-white px-4 py-2.5 mt-4 text-sm">
                  <CreditCard className="w-4 h-4" /> {paying === p.id ? "Redirecting…" : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-zinc-400 font-semibold mt-4">Secure checkout by Stripe. Prices exclude VAT — the exact total (incl. any VAT) is shown at checkout. Switching plans takes effect immediately after payment.</p>
    </div>
  );
}
